<script setup lang="ts">
import Breadcrumbs from '@/Components/Breadcrumbs.vue'
import ApplicationHead from '@/Components/Layout/ApplicationHead.vue'
import CartFeature from '@/Components/Product/CartFeature.vue'
import TextLink from '@/Components/TextLink.vue'
import ProductVariantCard from '@/Components/UI/ProductVariantCard.vue'
import Rating from '@/Components/UI/Rating.vue'
import WishlistButton from '@/Components/UI/WishlistButton.vue'
import { useJsonLd } from '@/Composables/json-ld'
import { wishlistAddedKey, wishlistDeletedKey } from '@/events/wishlist.event-key'
import { useCartStore } from '@/Stores/cart.store'
import { ArrowUturnLeftIcon, ChartPieIcon, CheckIcon, CursorArrowRaysIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { Product, ProductVariant } from '@interfaces/product.interface'
import { Review } from '@interfaces/review.interface'
import { useEventBus } from '@vueuse/core'
import { round, truncate } from 'lodash-es'
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ProductMedia } from './Interfaces/product-media.interface'
import { Product as ProductCustom } from './Interfaces/product.interface'
import { VariantItem } from './Interfaces/variant-item.interface'
import ProductBrand from './Partials/ProductBrand.vue'
import ProductCharacteristics from './Partials/ProductCharacteristics.vue'
import ProductDescription from './Partials/ProductDescription.vue'
import ProductGallery from './Partials/ProductGallery.vue'
import ProductReviews from './Partials/ProductReviews.vue'
import ProductReviewsSummary from './Partials/ProductReviewsSummary.vue'
import ProductShipping from './Partials/ProductShipping.vue'
import RingSizeChart from './Partials/RingSizeChart.vue'

const ProductTools = defineAsyncComponent(() => import('./Partials/ProductTools.vue'))
const ProductCard = defineAsyncComponent(() => import('@/Components/UI/ProductCard.vue'))

const props = defineProps<{
    description: string,
    product: ProductCustom,
    productOptions: Array<{
        name: string,
        value: string,
    }>,
    productCategories: Array<any>,
    media: Array<ProductMedia>,
    variants: {
        type: string,
        items: VariantItem[],
    },
    options: Array<any>,
    ratingAverage: number,
    reviewsCount: number,
    reviews: JsonResourcePaginated<Array<Review>>,
    reviewsSummary: Array<{
        rating: number,
        count: number,
        percentage: number,
    }>,
    openHours: {
        today: boolean,
        tomorrow: boolean,
        monday: boolean,
    },
    setProducts: Array<Product>,
    upsellProducts: Array<ProductVariant>,
    viewsCount?: number,
    paymentOptions: Record<string, {
        provider: string,
        min_amount: number,
    }>,
}>()

const { t, n } = useI18n()
const cartStore = useCartStore()

const { productSchema } = useJsonLd()

const productGallery = ref<InstanceType<typeof ProductGallery> | null>(null)

const isRingsCategory = computed(() => props.productCategories.map(({ code }) => code).includes('rings'))

const priceRange = computed(() => {
    let min = Math.min(...props.variants.items.map((item) => item.final_price))
    let max = Math.max(...props.variants.items.map((item) => item.final_price))

    if (!isFinite(min)) {
        min = 0
    }

    if (!isFinite(max)) {
        max = 0
    }
    const minFormatted = n(min, 'currency')
    const maxFormatted = n(max, 'currency')

    if (minFormatted !== maxFormatted) {
        return `${minFormatted} - ${maxFormatted}`
    }

    return minFormatted
})

const variantInitialAnimation = ref(true)
const variant = reactive<{
    productId: number | null,
    variantId: number | null,
    selected: VariantItem | null,
    inStock: boolean,
    deliveryDays: {
        from: number | null,
        to: number | null,
    },
    availableQuantity: number,
}>({
    productId: null,
    variantId: null,
    selected: null,
    inStock: true,
    deliveryDays: {
        from: null,
        to: null,
    },
    availableQuantity: 1,
})

const selectOption = (optionId: number, valueId: number, initial = false) => {
    const variant = props.variants.items.find((variant) => {
        return variant.options.every((option) => option.option_id === optionId && option.value_id === valueId)
    })

    setVariant(variant)

    // Track CustomizeProduct event for Facebook Pixel
    if (!initial && variant) {
        window.fbq('track', 'CustomizeProduct', {
            content_type: 'product',
            contents: [{
                id: `${variant.product_id}-${variant.id}`,
                item_price: variant.sale_price ?? variant.price,
            }],
        })
    }
}

const setVariant = (selectedVariant: any) => {
    variant.productId = selectedVariant?.product_id
    variant.variantId = selectedVariant?.id
    variant.selected = selectedVariant
    variant.inStock = selectedVariant?.quantity > 0
    variant.deliveryDays.from = selectedVariant?.delivery_days?.from
    variant.deliveryDays.to = selectedVariant?.delivery_days?.to
    variant.availableQuantity = selectedVariant?.quantity

    if (selectedVariant?.media_id) {
        productGallery.value?.showMedia(selectedVariant.media_id)
    }

    nextTick(() => variantInitialAnimation.value = true)
}

const productVariant = computed(() => {
    if (!variant.variantId) {
        return undefined
    }

    const productVariant = props.product.variants.find((item) => item.id === variant.variantId)

    if (!productVariant) {
        return undefined
    }

    return { id: productVariant.id, in_wishlist: productVariant.wishlist_exists } as ProductVariant
})

const cartForm = reactive({
    options: props.options.reduce((a, { id }) => ({ ...a, [id]: null }), {}),
    quantity: 1,
    loading: false,
    added: false,
})

if (props.options.length === 0) {
    setVariant(props.variants.items[0])

    variantInitialAnimation.value = false
} else if (props.options.length === 1) {
    const firstOption = props.options[0]

    if (firstOption.values.length === 1) {
        selectOption(firstOption.id, firstOption.values[0].id, true)

        cartForm.options[firstOption.id] = firstOption.values[0].id

        variantInitialAnimation.value = false
    }
}

const addToCart = () => {
    cartForm.loading = true
    cartForm.added = false

    cartStore.addItem(variant.productId!, variant.variantId!, cartForm.quantity)
        .then(() => cartForm.added = true)
        .finally(() => cartForm.loading = false)
}

// Temp hack: to make sure the product variant in the wishlist is updated
const wishlistAddedBus = useEventBus(wishlistAddedKey)
const wishlistDeletedBus = useEventBus(wishlistDeletedKey)

const handleWishlistAdded = ({ productVariantId }) => {
    const variant = props.product.variants.find((variant) => variant.id === productVariantId)

    if (variant) {
        variant.wishlist_exists = true
    }
}

const handleWishlistDeleted = ({ productVariantId }) => {
    const variant = props.product.variants.find((variant) => variant.id === productVariantId)

    if (variant) {
        variant.wishlist_exists = false
    }
}

onMounted(() => {
    wishlistAddedBus.on(handleWishlistAdded)
    wishlistDeletedBus.on(handleWishlistDeleted)
})

onBeforeUnmount(() => {
    wishlistAddedBus.off(handleWishlistAdded)
    wishlistDeletedBus.off(handleWishlistDeleted)
})
</script>

<template>
    <div>
        <ApplicationHead :title="product.title">
            <meta property="og:type" content="product">
            <meta property="og:title" :content="product.title">
            <meta property="og:description" :content="truncate(description, { length: 252 })">

            <meta v-if="media.length > 0" property="og:image" :content="media[0].size_800px.url">

            <meta property="product:locale" :content="$page.props.app.localization.locale">
            <meta property="product:condition" content="new">
            <meta property="product:availability" :content="product.in_stock ? 'instock' : 'out of stock'">
            <meta property="product:price:amount" :content="String(product.sale_price ?? product.retail_price)">
            <meta property="product:price:currency" content="EUR">
            <meta property="product:item_group_id" :content="`product_${product.id}`">

            <meta name="description" :content="truncate(description, { length: 150 })">
            <meta name="robots" content="max-snippet:-1, max-image-preview:large, max-video-preview:-1">

            <component :is="'script'" type="application/ld+json">
                {{ JSON.stringify(productSchema(
                    product,
                    description,
                    media.map((media) => media.size_800px.url),
                    variants.items,
                    {
                        reviews_count: reviewsCount,
                        rating_average: ratingAverage,
                    }
                )) }}
            </component>
        </ApplicationHead>

        <Breadcrumbs />

        <div class="max-w-2xl px-4 mx-auto mt-8 sm:px-6 lg:max-w-7xl lg:px-8">
            <div class="lg:grid lg:grid-cols-12 lg:items-start lg:gap-x-8">
                <div class="relative lg:col-span-6">
                    <ProductGallery ref="productGallery" :product="props.product" :media="props.media" />
                </div>

                <section class="mt-10 sm:mt-16 lg:mt-0 lg:col-span-6 lg:pl-16">
                    <!-- <h1 class="text-2xl font-bold tracking-tight sm:text-3xl text-grayscale-900">{{ product.title }}</h1> -->
                    <h1 class="font-bold text-h5 sm:text-h4 text-grayscale-900">{{ product.title }}</h1>

                    <div v-if="reviewsCount > 0" class="mt-2">
                        <h2 class="sr-only">{{ t('components.product.reviews') }}</h2>
                        <div class="flex items-center">
                            <div>
                                <Rating :rating="ratingAverage" />
                                <p class="sr-only">{{ t('components.review.stars-count', { count: round(ratingAverage) }) }}</p>
                            </div>

                            <p class="ml-2 text-p2 text-grayscale-500">{{ $t('components.product.reviews-count', reviewsCount) }}</p>
                        </div>
                    </div>

                    <div class="flex items-center mt-4">
                        <Transition
                            mode="out-in"
                            enter-active-class="transition duration-300 ease-out"
                            enter-from-class="-translate-y-1.5 opacity-0"
                            enter-to-class="translate-y-0 opacity-100"
                            leave-active-class="transition duration-100 ease-in"
                            leave-from-class="translate-y-0 opacity-100"
                            leave-to-class="translate-y-1.5 opacity-0">
                            <div :key="variant.selected?.final_price" class="whitespace-nowrap">
                                <template v-if="!variant.selected">
                                    <span class="text-2xl text-grayscale-800">{{ priceRange }}</span>
                                </template>

                                <template v-else-if="variant.selected.sale_price">
                                    <span class="text-xl line-through text-grayscale-500">{{ n(variant.selected.price, 'currency') }}</span>
                                    <span class="ml-2 text-2xl font-medium text-red-600">{{ n(variant.selected.sale_price, 'currency') }}</span>
                                </template>

                                <template v-else>
                                    <span class="text-2xl text-grayscale-800">{{ n(variant.selected.price, 'currency') }}</span>
                                </template>

                            </div>
                        </Transition>
                    </div>

                    <div class="mt-8 lg:col-span-5">
                        <div class="space-y-8">
                            <div v-for="(option, optionIdx) in options" :key="optionIdx">
                                <div class="flex items-baseline justify-between">
                                    <h2 class="text-p3 text-grayscale-500">{{ option.name }}</h2>

                                    <RingSizeChart v-if="isRingsCategory" />
                                </div>

                                <fieldset class="mt-2">
                                    <legend class="sr-only">{{ option.name }}</legend>
                                    <div class="flex flex-wrap gap-3">
                                        <label
                                            v-for="(value, valueIdx) in option.values"
                                            :key="valueIdx"
                                            :class="[
                                                'relative group rounded',
                                                option.type === 'image' ? 'p-1' : 'py-2 px-2.5',
                                                'flex items-center justify-center text-sm uppercase overflow-hidden transition focus:outline-none',
                                                !value.available ? 'cursor-not-allowed' : 'cursor-pointer',
                                                cartForm.options[option.id] == value.id ? 'ring-2 ring-primary-600 font-medium' : ['bg-white ring-1 ring-grayscale-100 text-grayscale-800', value.available && 'hover:ring-grayscale-200']
                                            ]">
                                            <input v-model="cartForm.options[option.id]" type="radio" :name="`choice-${option.id}`" :value="value.id" class="sr-only" :aria-labelledby="`choice-${option.id}-label-${value.id}`" @change="selectOption(option.id, value.id)">
                                            <span :id="`choice-${option.id}-label-${value.id}`" :class="[!value.available && 'opacity-25']">
                                                <template v-if="option.type === 'image' && value.media_id && media.map((media) => media.id).includes(value.media_id)">
                                                    <img class="object-cover w-12 h-12" :src="media.find((media) => media.id === value.media_id)?.size_800px?.url" :alt="value.name" />
                                                </template>

                                                <template v-else>
                                                    {{ value.name }}
                                                </template>
                                            </span>

                                            <div v-if="option.type === 'image' && value.media_id && media.map((media) => media.id).includes(value.media_id)" class="absolute inset-0 bg-black bg-opacity-0 transition-opacity group-hover:bg-opacity-[0.02]"></div>
                                            <svg v-if="!value.available" class="absolute z-10 w-full h-full stroke-[1.5] text-grayscale-100" stroke="currentColor">
                                                <line x1="0" y1="100%" x2="100%" y2="0" />
                                            </svg>
                                        </label>
                                    </div>
                                </fieldset>
                            </div>
                        </div>

                        <div class="mt-8">
                            <Transition
                                mode="out-in"
                                enter-active-class="transition duration-300 ease-out"
                                enter-from-class="translate-x-3 opacity-0"
                                enter-to-class="translate-x-0 opacity-100"
                                leave-active-class="transition duration-200 ease-in"
                                leave-from-class="translate-x-0 opacity-100"
                                leave-to-class="translate-x-3 opacity-0">
                                <template v-if="variant.selected && variant.inStock">
                                    <div class="flex flex-wrap items-center mt-6 text-p3 text-grayscale-500 gap-x-1.5">
                                        <CheckIcon class="flex-shrink-0 w-4 h-4" :class="{ 'text-success-600': !variant.selected.delivery_days.from, 'text-yellow-500': variant.selected.delivery_days.from }" />
                                        <p>{{ t('components.product.in-stock') }}</p>

                                        <template v-if="variant.selected?.delivery_days?.from">
                                            <span class="text-xl leading-[18px] text-grayscale-200" aria-hidden="true">&middot;</span>
                                            <i18n-t tag="span" keypath="components.product.ship-within" scope="global">
                                                <span>{{ variant.selected?.delivery_days?.from }}-{{ variant.selected?.delivery_days?.to }}</span>
                                            </i18n-t>
                                        </template>

                                        <template v-else-if="variant.availableQuantity > 0 && variant.availableQuantity < 3">
                                            <span class="text-xl leading-[18px] text-grayscale-200" aria-hidden="true">&middot;</span>
                                            <p v-if="variant.availableQuantity === 1">{{ $t('pages.product.left-in-stock-one') }}</p>
                                            <p v-else>{{ $t('pages.product.left-in-stock-few', variant.availableQuantity) }}</p>
                                        </template>
                                    </div>
                                </template>

                                <template v-else-if="variant.selected && !variant.inStock">
                                    <div class="flex items-center mt-6 text-p3 text-grayscale-500 gap-x-1.5">
                                        <XMarkIcon class="w-4 h-4 text-error-600" />
                                        <p class="whitespace-nowrap">{{ t('components.product.out-of-stock') }}</p>
                                        <span class="text-xl leading-[18px] text-grayscale-200" aria-hidden="true">&middot;</span>
                                        <TextLink class="" :href="route('contact', { product_id: product.id })">{{ t('pages.product.contact-us') }}</TextLink>
                                    </div>
                                </template>

                                <template v-else-if="!variant.selected">
                                    <div class="flex items-center mt-6 text-p3 text-grayscale-500 gap-x-1.5">
                                        <CursorArrowRaysIcon class="w-4 h-4 text-grayscale-500" />
                                        <div class="whitespace-nowrap">{{ $t('pages.product.select-option', { option: options.length > 0 ? options.map((o) => o.name).join(', ').toLowerCase() : $t('pages.product.select-option-fallback') }) }}</div>
                                    </div>
                                </template>
                            </Transition>

                            <RingSizeChart v-if="variants.type === 'single' && isRingsCategory" class="mt-1" />

                            <div class="mt-4">
                                <div class="flex gap-x-4">
                                    <button type="submit" class="flex items-center justify-center w-full px-8 py-3 text-base font-medium text-white transition duration-150 ease-in-out border border-transparent rounded bg-primary-600 hover:bg-primary-700 disabled:bg-primary-600 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2" :class="[cartForm.loading && 'cursor-not-allowed']" :disabled="cartForm.loading || !variant.selected || !variant.inStock" @click="addToCart()">
                                        <span>{{ cartForm.added ? t('components.product.added-to-cart') : t('components.product.add-to-cart') }}</span>
                                    </button>

                                    <WishlistButton v-if="productVariant" :product-variant="productVariant" />
                                </div>
                            </div>
                        </div>

                        <div class="mt-8 space-y-2">
                            <CartFeature v-if="(variant.selected?.final_price || 0) >= paymentOptions.esto_x?.min_amount" :appear="variantInitialAnimation" :icon="ChartPieIcon" color="mint">
                                <i18n-t tag="span" class="block leading-4" keypath="components.cart.features.pay3.title" scope="global">
                                    <Transition
                                        mode="out-in"
                                        enter-active-class="transition duration-300 ease-out"
                                        enter-from-class="-translate-y-1.5 opacity-0"
                                        enter-to-class="translate-y-0 opacity-100"
                                        leave-active-class="transition duration-100 ease-in"
                                        leave-from-class="translate-y-0 opacity-100"
                                        leave-to-class="translate-y-1.5 opacity-0">
                                        <span :key="variant.selected?.final_price" class="inline-block whitespace-nowrap">{{ $n(variant.selected!.final_price / 3, 'currency') }}</span>
                                    </Transition>
                                </i18n-t>
                                <!-- <Link :href="route('payment-methods')" class="border-b border-dashed border-grayscale-800 hover:border-grayscale-900 hover:border-solid text-p3">Learn more</Link> -->
                            </CartFeature>

                            <CartFeature :icon="ArrowUturnLeftIcon" :hint-area-label="$t('components.cart.features.easy-return.title')">
                                <span>{{ $t('components.cart.features.easy-return.title') }}</span>

                                <template #hint>
                                    <span class="font-medium text-grayscale-900 text-p2">{{ $t('components.cart.features.easy-return.note-title') }}</span>
                                    <i18n-t tag="p" class="mt-2" keypath="components.cart.features.easy-return.note-body" scope="global">
                                        <TextLink :href="route('returns')"> {{ $t('components.cart.features.easy-return.note-return-policy') }}</TextLink>
                                    </i18n-t>
                                </template>
                            </CartFeature>
                        </div>

                        <div class="mt-4">
                            <span class="text-p2 text-grayscale-500">{{ t('pages.product.share') }}</span>

                            <ul role="list" class="flex items-center mt-4 space-x-6">
                                <li>
                                    <a class="flex items-center justify-center w-5 h-5 text-grayscale-400 hover:text-grayscale-500" :href="`https://www.facebook.com/sharer/sharer.php?u=${route('product', product.slug_path)}`" :onclick="`window.open('https://www.facebook.com/sharer/sharer.php?u=${route('product', product.slug_path)}','','width=900, height=500, toolbar=no, status=no'); return(false);`">
                                        <span class="sr-only">Share on Facebook</span>
                                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                            <path fill-rule="evenodd" d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clip-rule="evenodd" />
                                        </svg>
                                    </a>
                                </li>

                                <li>
                                    <a class="flex items-center justify-center w-5 h-5 text-grayscale-400 hover:text-grayscale-500" :href="`https://twitter.com/intent/tweet?text=Tweet%20this&url=${route('product', product.slug_path)}`" :onclick="`window.open('https://twitter.com/intent/tweet?text=Tweet%20this&url=${route('product', product.slug_path)}','','width=650, height=350, toolbar=no, status=no'); return(false);`">
                                        <span class="sr-only">Share on Twitter</span>
                                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                            <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                                        </svg>
                                    </a>
                                </li>

                                <li>
                                    <a class="flex items-center justify-center w-5 h-5 text-grayscale-400 hover:text-grayscale-500" :href="`https://pinterest.com/pin/create/bookmarklet/?media=${media[0]?.size_800px?.url}&url=${route('product', product.slug_path)}`" :onclick="`window.open('https://pinterest.com/pin/create/bookmarklet/?media=${media[0]?.size_800px?.url}&url=${route('product', product.slug_path)}','','width=650, height=350, toolbar=no, status=no'); return(false);`">
                                        <span class="sr-only">Share on Pinterest</span>
                                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
                                            <path d="M0,128.002 C0,180.416 31.518,225.444 76.619,245.241 C76.259,236.303 76.555,225.573 78.847,215.848 C81.308,205.457 95.317,146.1 95.317,146.1 C95.317,146.1 91.228,137.927 91.228,125.848 C91.228,106.879 102.222,92.712 115.914,92.712 C127.557,92.712 133.182,101.457 133.182,111.929 C133.182,123.633 125.717,141.14 121.878,157.355 C118.671,170.933 128.686,182.008 142.081,182.008 C166.333,182.008 182.667,150.859 182.667,113.953 C182.667,85.899 163.772,64.901 129.405,64.901 C90.577,64.901 66.388,93.857 66.388,126.201 C66.388,137.353 69.676,145.217 74.826,151.307 C77.194,154.104 77.523,155.229 76.666,158.441 C76.052,160.796 74.642,166.466 74.058,168.713 C73.206,171.955 70.579,173.114 67.649,171.917 C49.765,164.616 41.436,145.031 41.436,123.015 C41.436,86.654 72.102,43.054 132.918,43.054 C181.788,43.054 213.953,78.418 213.953,116.379 C213.953,166.592 186.037,204.105 144.887,204.105 C131.068,204.105 118.069,196.635 113.616,188.15 C113.616,188.15 106.185,217.642 104.611,223.337 C101.897,233.206 96.585,243.07 91.728,250.758 C103.24,254.156 115.401,256.007 128.005,256.007 C198.689,256.007 256.001,198.698 256.001,128.002 C256.001,57.309 198.689,0 128.005,0 C57.314,0 0,57.309 0,128.002 Z"></path>
                                        </svg>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>

            <div class="grid grid-cols-1 pb-16 mt-12 gap-y-6 gap-x-6 lg:grid-cols-2 sm:pb-24">
                <div class="space-y-6 lg:order-1">
                    <ProductCharacteristics :product="product" :variant="variant.selected" :options="productOptions" />
                    <ProductBrand v-if="product.brand" :brand="product.brand" />
                    <ProductDescription :product="product" />
                    <ProductTools v-if="$page.props.user?.is_admin" :product="product" :views-count="viewsCount!" />
                </div>

                <div class="space-y-6">
                    <ProductShipping />
                    <ProductReviewsSummary :rating-average="ratingAverage" :reviews-count="reviewsCount" :summary="reviewsSummary" />
                    <ProductReviews v-if="reviews.data.length > 0" :reviews="reviews" />
                </div>
            </div>

            <section v-if="setProducts.length > 0" aria-labelledby="product-set-heading" class="pb-16 sm:pb-24">
                <h2 id="product-set-heading" class="text-xl font-bold text-slate-900">{{ t('pages.product.product-set') }}</h2>

                <div class="grid grid-cols-2 mt-6 gap-y-10 gap-x-6 lg:grid-cols-4 lg:gap-x-8">
                    <ProductCard
                        v-for="(setProduct) in setProducts"
                        :key="setProduct.id"
                        :product="setProduct"
                        name-tag="div" />
                </div>
            </section>

            <section v-if="upsellProducts.length > 0" aria-labelledby="upsell-heading" class="pb-16 sm:pb-24">
                <h2 id="upsell-heading" class="text-xl font-bold text-slate-900">{{ t('pages.product.upsell-title') }}</h2>

                <div class="grid grid-cols-2 mt-6 gap-y-10 gap-x-6 lg:grid-cols-4 lg:gap-x-8">
                    <ProductVariantCard
                        v-for="(upsellProduct, index) in upsellProducts"
                        :key="upsellProduct.id"
                        :product-variant="upsellProduct"
                        :item-list="{ id: 'upsell-products', name: 'Upsell', index }"
                        name-tag="div" />
                </div>
            </section>
        </div>
    </div>
</template>
